/**
 * Firebase Analytics for the public Super Picker SPA (share-link recipient
 * mode).
 *
 * Mirrors `super-picker/services/analytics.ts` but with two key differences:
 *
 *   - **No CEA**: recipients are external users, not licensed agents. The
 *     equivalent identity is the share **uuid** they landed on and the
 *     **masked mobile** the backend resolved for them. Both are stashed as
 *     Firebase user properties so every downstream event is implicitly
 *     attributable to the invite.
 *
 *   - **Subset of events**: we only instrument features that actually exist
 *     on the public side (Project Details, Project Research, factsheet /
 *     brochure, floor plan, tower view, site plan). Agent-only events
 *     (e.g. comparable search, training) are intentionally absent.
 *
 * Firebase config reads the same `REACT_APP_FIREBASE_*` env vars as the
 * agent app so a single Firebase project receives both audiences and they
 * can be segmented in Analytics by the `share_uuid` user property.
 */
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  Analytics,
  getAnalytics,
  logEvent as firebaseLogEvent,
  setUserProperties,
} from 'firebase/analytics';
import { isDevelopment } from '../../config/env';

const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'transformation-office';
const apiKey = process.env.REACT_APP_FIREBASE_API_KEY;
const appId = process.env.REACT_APP_FIREBASE_APP_ID;
const measurementId = process.env.REACT_APP_FIREBASE_MEASUREMENT_ID;

const firebaseConfig = apiKey && appId
  ? {
      apiKey,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || `${PROJECT_ID}.firebaseapp.com`,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || `${PROJECT_ID}.appspot.com`,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
      appId,
      ...(measurementId ? { measurementId } : {}),
    }
  : null;

let app: FirebaseApp | null = null;
let analytics: Analytics | null = null;

/**
 * Cached share context. Same rationale as the agent's `cachedCeaNumber` —
 * user properties are write-only in Firebase, so we keep our own copy to
 * stamp each event payload with `share_uuid` / `recipient_mobile_masked`.
 * That lets reports filter / join by invite without depending on
 * user-property reporting.
 */
let cachedShareUuid: string | null = null;
let cachedRecipientMobileMasked: string | null = null;

export function initPublicAnalytics(): void {
  if (app || !firebaseConfig) return;
  try {
    app = initializeApp(firebaseConfig);
    analytics = getAnalytics(app);
  } catch (e) {
    if (isDevelopment) {
      console.warn('[public-analytics] init skipped', e);
    }
  }
}

function safeLogEvent(name: string, params?: Record<string, string | number | boolean>): void {
  // Always merge the share context (when known) so downstream queries can
  // attribute the event back to the invite even if Analytics
  // user-property reporting is unavailable.
  const merged: Record<string, string | number | boolean> = {
    ...(params || {}),
    ...(cachedShareUuid ? { share_uuid: cachedShareUuid } : {}),
    ...(cachedRecipientMobileMasked
      ? { recipient_mobile_masked: cachedRecipientMobileMasked }
      : {}),
  };
  if (isDevelopment) {
    console.info('[public-analytics]', name, merged);
  }
  if (!analytics) return;
  try {
    firebaseLogEvent(analytics, name, merged as Record<string, unknown>);
  } catch (e) {
    if (isDevelopment) {
      console.warn('[public-analytics] logEvent failed', name, e);
    }
  }
}

/**
 * Persist the share context so every subsequent event can attribute itself
 * to this invite. Called by `SuperPickerPublicApp` once the share resolve
 * succeeds. Safe to call repeatedly — re-setting the same value is a no-op.
 */
export function setPublicAnalyticsShareContext(input: {
  uuid: string | null | undefined;
  mobileMasked: string | null | undefined;
}): void {
  const uuid = input.uuid?.trim() || null;
  const mobile = input.mobileMasked?.trim() || null;
  cachedShareUuid = uuid;
  cachedRecipientMobileMasked = mobile;
  if (!analytics) return;
  try {
    setUserProperties(analytics, {
      share_uuid: uuid ?? '',
      recipient_mobile_masked: mobile ?? '',
    });
  } catch (e) {
    if (isDevelopment) {
      console.warn('[public-analytics] setUserProperties failed', e);
    }
  }
}

export function getCachedPublicShareUuid(): string | null {
  return cachedShareUuid;
}

// ---------------------------------------------------------------------------
// Per-feature event helpers
//
// Mirror the agent's `super-picker/services/analytics.ts` helper surface, but
// only for features that exist on the public side. Each helper attaches the
// share context implicitly via `safeLogEvent` above.
// ---------------------------------------------------------------------------

/**
 * The recipient successfully landed on the public SPA via a granted share.
 * Fired once per page-load after share-resolve completes.
 */
export function logShareLandingViewed(input: {
  module: string;
  projectId?: string | null;
}): void {
  safeLogEvent('picker_public_share_landing_viewed', {
    module: input.module,
    ...(input.projectId ? { project_id: input.projectId } : {}),
  });
}

type PublicUnitTrackingInput = {
  projectName?: string | null;
  projectId?: string | number | null;
  unitNumber?: string | null;
  unitId?: string | number | null;
  source: string;
};

function toTrackingBaseParams(input: PublicUnitTrackingInput): Record<string, string | number | boolean> {
  return {
    source: input.source,
    ...(input.projectName ? { project_name: input.projectName } : {}),
    ...(input.projectId !== undefined && input.projectId !== null
      ? { project_id: String(input.projectId) }
      : {}),
    ...(input.unitNumber ? { unit_number: input.unitNumber } : {}),
    ...(input.unitId !== undefined && input.unitId !== null
      ? { unit_id: String(input.unitId) }
      : {}),
  };
}

export function logPublicProjectViewed(
  projectName: string | null | undefined,
  source: string,
  projectId?: string | number | null,
): void {
  safeLogEvent('picker_public_project_viewed', toTrackingBaseParams({ projectName, projectId, source }));
}

export function logPublicProjectResearchTabViewed(): void {
  safeLogEvent('picker_public_project_research_tab_viewed', { tab_name: 'project_research' });
}

export function logPublicSitePlanOpened(
  projectName: string | null | undefined,
  source: string,
  projectId?: string | number | null,
): void {
  safeLogEvent(
    'picker_public_site_plan_opened',
    toTrackingBaseParams({ projectName, projectId, source }),
  );
}

export function logPublicTowerViewOpened(input: PublicUnitTrackingInput): void {
  safeLogEvent('picker_public_tower_view_opened', toTrackingBaseParams(input));
}

export function logPublicFloorPlanOpened(input: PublicUnitTrackingInput): void {
  safeLogEvent('picker_public_floor_plan_opened', toTrackingBaseParams(input));
}

export function logPublicFloorPlanComparisonOpened(input: PublicUnitTrackingInput): void {
  safeLogEvent('picker_public_floor_plan_comparison_opened', toTrackingBaseParams(input));
}

export function logPublicFactsheetViewed(
  projectName?: string | null,
  projectId?: string | number | null,
): void {
  safeLogEvent(
    'picker_public_factsheet_viewed',
    toTrackingBaseParams({ projectName, projectId, source: 'factsheet' }),
  );
}

export function logPublicBrochureViewed(
  projectName?: string | null,
  projectId?: string | number | null,
): void {
  safeLogEvent(
    'picker_public_brochure_viewed',
    toTrackingBaseParams({ projectName, projectId, source: 'brochure' }),
  );
}
